import { Component, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { LanguageMatchRepoService } from '@client/app/services/api/language-match-repo/language-match-repo.service';
import { LanguageMatchGetSummaryResponse } from '@client/app/services/api/language-match-repo/language-match-repo.type';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonUIRouterTransitionService } from '@monsido/services/ui-router-transition-service/ui-router-transition-service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { SessionService } from '@monsido/core/session/session.service';

@Component({
    selector: 'language-match-summary',
    standalone: false,
    templateUrl: './language-match-summary.component.html',
})
export class LanguageMatchSummaryComponent implements OnInit {
    summary: LanguageMatchGetSummaryResponse = {} as LanguageMatchGetSummaryResponse;
    isFiltersOpen: boolean = false;
    showFilters: boolean = false;
    filterType: any = null;
    loading: boolean = false;
    filters: any = {};
    availableFilters: any = {};
    filterTypesName: string = '';

    // Cache chart data to prevent infinite loops
    private _chartLabels: string[] = [];
    private _chartData: number[] = [];
    private _chartSeries: any[] = [];
    private _chartColors: string[] = [];
    private _chartOptions: any = {};
    private _chartDataInitialized: boolean = false;

    constructor (
        private languageMatchRepoService: LanguageMatchRepoService,
        private pipeService: PipeService,
        private stateService: StateService,
        private activeFeatures: ActiveFeatureService,
        private translateService: TranslateService,
        private monUIRouterTransitionService: MonUIRouterTransitionService,
        private sessionService: SessionService,
    ) {}

    ngOnInit (): void {
        this.summary = {
            pages_with_tags: 0,
            pages_without_tags: 0,
            pages_with_matched_language_tag: 0,
            pages_with_mismatched_language_tag: 0,
            pages_with_multi_language_tag: 0,
            detected_languages: {},
            declared_languages: {},
            total_pages: 0,
        };
        this.isFiltersOpen = false;
        this.showFilters = this.activeFeatures.isFeatureActive('table_filters');
        this.filterType = null;
        this.loading = false;
        this.getSummary();
        this.setupFilters();
    }

    get isLanguageAttributeEnabled (): boolean {
        return this.sessionService.domain?.scan?.use_lang_attribute === true;
    }

    getSummary (): void {
        const params = {};
        this.loading = true;
        this.languageMatchRepoService.summary(params)
            .then((data: LanguageMatchGetSummaryResponse) => {
                this.summary = data;

                // Initialize chart data after getting summary
                this.initializeChartData();
            })
            .catch((error: any) => {
                // eslint-disable-next-line no-console
                console.error('Language Validation summary: API Error:', error);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    onFiltersChanges (changes: any): void {
        if (changes[this.filterTypesName].length > 0) {
            this.filters[this.filterTypesName] = [changes[this.filterTypesName][changes[this.filterTypesName].length - 1]];
            this.filterType = this.filters[this.filterTypesName][0].value;
        } else {
            this.filterType = null;
        }
        this.getSummary();
    }

    setupFilters (): void {
        this.filterTypesName = this.translateService.getString('Type');
        this.filters = {};
        this.filters[this.filterTypesName] = [];
        this.availableFilters = {};
        this.availableFilters[this.filterTypesName] = [
            {
                name: this.translateService.getString('HTML'),
                value: 'html',
            },
            {
                name: this.translateService.getString('PDF'),
                value: 'pdf',
            },
            {
                name: this.translateService.getString('Documents'),
                value: 'document',
            },
        ];
    }

    getTotalPagesCalculated (): number {
        if (this.summary.total_pages) {
            return this.summary.total_pages;
        }
        // Calculate from pages_with_tags + pages_without_tags if available
        if (this.summary.pages_with_tags !== undefined && this.summary.pages_without_tags !== undefined) {
            return this.summary.pages_with_tags + this.summary.pages_without_tags;
        }
        // Fallback to detected languages count
        if (this.summary.detected_languages) {
            return Object.values(this.summary.detected_languages).reduce((sum, count) => sum + count, 0);
        }
        return 0;
    }

    getPagesWithIssues (): number {
        // Pages with issues are those with mismatched or multi-language tags
        return (this.summary.pages_with_mismatched_language_tag || 0) +
               (this.summary.pages_with_multi_language_tag || 0);
    }

    hasLanguageTagData (): boolean {
        return !!(this.summary.pages_with_tags ||
                 this.summary.pages_without_tags ||
                 this.summary.pages_with_matched_language_tag ||
                 this.summary.pages_with_mismatched_language_tag ||
                 this.summary.pages_with_multi_language_tag);
    }

    getLanguageTagChartLabels (): string[] {
        return this._chartLabels;
    }

    getLanguageTagChartData (): number[] {
        return this._chartData;
    }

    getLanguageTagChartSeries (): any[] {
        return this._chartSeries;
    }

    getLanguageTagChartColors (): string[] {
        return this._chartColors;
    }

    getLanguageTagChartOptions (): any {
        return this._chartOptions;
    }

    private initializeChartData (): void {
        if (this._chartDataInitialized) {
            return;
        }

        this._chartLabels = [
            this.translateService.getString('Pages without issues'),
            this.translateService.getString('Pages with issues'),
            this.translateService.getString('Pages with multiple languages detected'),
            this.translateService.getString('Pages with insufficient text for language detection'),
        ];

        this._chartData = [
            this.summary.pages_with_matched_language_tag || 0,
            this.summary.pages_with_mismatched_language_tag || 0,
            this.summary.pages_with_multi_language_tag || 0,
            this.summary.pages_without_tags || 0,
        ];

        this._chartSeries = [{
            name: 'Pages',
            data: this._chartData,
        }];

        this._chartColors = [
            '#28a745', // Green for matched
            '#dc3545', // Red for issues
            '#ffc107', // Yellow for multi-language
            '#6c757d', // Gray for no language detected
        ];

        const maxPages = Math.max(
            ...this._chartData,
        );
        const stepSize = Math.ceil(maxPages / 10) || 1;

        this._chartOptions = {
            scales: {
                xAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            callback: (value: number) => {
                                return Math.floor(value);
                            },
                            stepSize,
                        },
                    },
                ],
                yAxes: [
                    {
                        gridLines: {
                            display: false,
                        },
                    },
                ],
            },
            legend: {
                display: false,
            },
            tooltips: {
                callbacks: {
                    label: (tooltipItem: any, data: any) => {
                        const label = data.datasets[tooltipItem.datasetIndex].label || '';
                        const value = Math.floor(tooltipItem.xLabel);
                        return label + ': ' + value;
                    },
                },
            },
        };

        this._chartDataInitialized = true;
    }

    /**
     * Get the correct total pages count based on pages_with_tags + pages_without_tags
     */
    getTotalPagesCount (): number {
        if (!this.summary) {
            return 0;
        }
        return (this.summary.pages_with_tags || 0) + (this.summary.pages_without_tags || 0);
    }
}
